import { useEffect, useState, type ReactNode } from 'react';
import { signOut } from 'firebase/auth';
import {
  AppBar,
  Box,
  Button,
  Chip,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';
import { auth } from './firebase';
import { ATTENTION_CAP, watchAttention } from './services/notifications';

const NAV = [
  { to: '/orders', label: 'Orders' },
  { to: '/catalog/products', label: 'Products' },
  { to: '/catalog', label: 'Categories' },
  { to: '/media', label: 'Media library' },
  { to: '/print-lab', label: 'Print lab' },
  { to: '/reels', label: 'Reels' },
  { to: '/banners', label: 'Banners' },
  { to: '/vendors', label: 'Vendors' },
  { to: '/artists', label: 'Artists' },
  { to: '/referrals', label: 'Referrals' },
  { to: '/commissions', label: 'Commissions' },
  { to: '/ratings', label: 'Ratings' },
  { to: '/crm', label: 'CRM' },
  { to: '/coupons', label: 'Coupons' },
  { to: '/loyalty', label: 'Print Credits' },
  { to: '/money', label: 'Money' },
  { to: '/reports', label: 'Reports' },
  { to: '/notifications', label: 'Email' },
];

const DRAWER_WIDTH = 220;

/**
 * What the Email badge is counting, in words.
 *
 * Two different failures share one number because they share one page, but they
 * are not the same thing and the tooltip must not blur them: one is our mail
 * not arriving, the other is a customer's mail arriving and going nowhere.
 */
function attentionLabel({ parked, failed }: { parked: number; failed: number }): string {
  const parts = [];
  if (parked) parts.push(`${parked > ATTENTION_CAP ? `over ${ATTENTION_CAP}` : parked} email ${parked === 1 ? 'reply' : 'replies'} could not be placed on an order`);
  if (failed) parts.push(`${failed > ATTENTION_CAP ? `over ${ATTENTION_CAP}` : failed} ${failed === 1 ? 'message' : 'messages'} could not be delivered`);
  return parts.join(' · ');
}

export function Shell({ children }: { children: ReactNode }) {
  const location = useLocation();

  /* The one listener pair in the whole console that runs on every page. It is
     here rather than on the Email page because a count you only see once you
     have opened the page is a count that changes nothing. */
  const [attention, setAttention] = useState({ parked: 0, failed: 0 });
  useEffect(() => watchAttention(setAttention), []);
  const waiting = attention.parked + attention.failed;

  // Longest matching prefix wins, so /catalog/products highlights "Products"
  // rather than lighting up "Categories" as well.
  const activePath = NAV.map((n) => n.to)
    .filter((to) => location.pathname === to || location.pathname.startsWith(`${to}/`))
    .sort((a, b) => b.length - a.length)[0];

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <AppBar
        position="fixed"
        color="inherit"
        sx={{ zIndex: (t) => t.zIndex.drawer + 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1, fontSize: '1rem', fontWeight: 700 }}>
            EPrintSmart Admin
          </Typography>
          <Button size="small" onClick={() => signOut(auth)}>
            Sign out
          </Button>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        <Toolbar />
        <List sx={{ px: 1 }}>
          {NAV.map((item) => (
            <ListItemButton
              key={item.to}
              component={NavLink}
              to={item.to}
              selected={activePath === item.to}
              sx={{ borderRadius: 1.5, mb: 0.25 }}
            >
              <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.9rem' }} />
              {/* Only ever on Email, and only when there is something to do.
                  A badge that is always present is wallpaper. */}
              {item.to === '/notifications' && waiting > 0 && (
                <Tooltip title={attentionLabel(attention)}>
                  <Chip
                    size="small"
                    color={attention.failed > 0 ? 'error' : 'warning'}
                    label={waiting > ATTENTION_CAP ? `${ATTENTION_CAP}+` : waiting}
                    sx={{ height: 20, minWidth: 28, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                  />
                </Tooltip>
              )}
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: 'background.default' }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}
